import React, { useEffect } from 'react';
import { reportToSegment, types } from '@smartcar/morse';
import { arrayOf, bool, func, object, shape, string } from 'prop-types';
import { Box, InputLabel, styled, Typography } from '@mui/material';

import ApplicationHeader from '../../../ApplicationHeader';
import { CopyButton, InlineLinks, InputField, PermissionGate, TabbedCodeBlock } from '../../../../../../components';

import staticText from '../../../../../../localization/Application/webhooks';
import { ActionMenu, Status } from '../WebhooksStatus/components/WebhooksStatusRow/components';
import { capitalizeFirstLetter } from '../../../../utils';
import SignalsList from '../WebhookForm/components/WebhookReview/components/SignalsList/SignalsList';
import { formatPermissionsByType } from '../WebhookInformationModal/WebhookInformationModal';
import { getSelectedSignalCount } from '../WebhookForm/util';
import { ACTIONS, getActions } from '../../utils/webhookConfig';

const ActionMenuWrapper = styled('div')(({ theme }) => ({
  position: 'absolute',
  right: '36px',
  button: {
    border: `1px solid ${theme.palette.grey[300]}`,
    borderRadius: theme.border.radius2,
    width: '36px',
    height: '36px',
  },
}));

const WebhookDetails = ({
  applicationId,
  signals,
  heading,
  webhook,
  testEvent,
  fetchTestEventRequest,
  errorFetchingTestEvent,
  toggleWebhookModal,
}) => {
  const getDataValue = ({ key, type, fallback }) => {
    let value = webhook[key];
    if (key === 'errorCallbackUri' && !webhook[key]) {
      return (
        <Typography variant="body2" sx={{ fontStyle: 'italic', color: 'text.disabled' }}>
          {staticText.webhookDetails.errorCallbackUriFallback}
        </Typography>
      );
    }
    if (value === 0) {
      return fallback ? (
        <Typography variant="body2"><InlineLinks text={fallback} /></Typography>
      ) : null;
    }
    if (key === 'autoSubscribe') {
      value = value === 'all' ? 'Subscribe all current and new connections' : 'Don’t subscribe any vehicles';
    }
    // Temporary override for existing imperial webhooks until we support imperial units
    if (key === 'units' && value === 'imperial') {
      value = 'imperial (Not currently supported - all values will be in metric)';
    }

    switch (type) {
      case 'snippet':
        return (
          <InputField
            id={key}
            inputValues={webhook[key]}
            inputReadOnly
            actions={(
              <CopyButton content={webhook[key]} />
            )}
          />
        );
      case 'chip':
        return (
          <Status statusText={capitalizeFirstLetter(value.toLowerCase())} />
        );
      default:
        return (
          <Typography variant="body2">
            {typeof value === 'string' ? capitalizeFirstLetter(value) : value}
          </Typography>
        );
    }
  };

  const getDataLabel = (item) => {
    let value = webhook[item.key];
    // show the empty errorCallbackUri for v4
    if (
      item.key === 'errorCallbackUri' &&
      webhook.version === '4.0' &&
      !value
    ) {
      value = true;
    }
    return value === undefined || value === null ?
      null : (
        <Box key={item.key}>
          <InputLabel htmlFor={item.key}>{item.label}</InputLabel>
          {getDataValue(item)}
        </Box>
      );
  };

  const openModal = (modalName) => {
    toggleWebhookModal({ nextModal: modalName, curModal: null });
  };

  useEffect(() => {
    fetchTestEventRequest(webhook.id);
  }, [webhook]);

  useEffect(() => {
    reportToSegment(types.PAGE, 'Webhook Details');
  }, []);

  const formattedPermissions = webhook.version === '4.0' ? null : formatPermissionsByType(webhook.permissions, webhook.type, webhook.profile).join(' ');

  return (
    <React.Fragment>
      <Box display="flex" justifyContent="space-between" alignItems="center">
        <ApplicationHeader
          heading={heading}
          subpageHeading={webhook.name}
          backLink={{
            path: `/apps/${applicationId}/webhooks`,
          }}
        />
        <ActionMenuWrapper>
          <PermissionGate
            dashboardPermission="write_webhooks"
            alternateComponent={
              <ActionMenu
                webhookId={webhook.id}
                actions={[]}
                openModal={openModal}
                disabled
              />
            }
          >
            <ActionMenu
              webhookId={webhook.id}
              actions={getActions(webhook.status).filter(({ key }) => key !== ACTIONS.VIEW.key)}
              openModal={openModal}
            />
          </PermissionGate>
        </ActionMenuWrapper>
      </Box>
      <Box display="flex" gap={12}>
        <Box display="flex" flexDirection="column" gap={3} width="480px">
          {staticText.webhookDetails.data.map(item => getDataLabel(item))}
          <div>
            {webhook.version === '4.0' ? (
              <SignalsList
                title={staticText.webhookDetails.signals}
                signals={signals}
                selectedSignals={new Set(webhook.signalNames)}
                selectedSignalCount={getSelectedSignalCount(signals, webhook.signalNames)}
              />
            ) : (
              <InputField
                label={staticText.webhookDetails.permissions}
                inputValues={formattedPermissions}
                inputReadOnly
                actions={(
                  <CopyButton content={formattedPermissions} />
                )}
              />
            )}
          </div>
        </Box>
        <Box width="480px" minWidth="480px">
          {/* Clean up when v3 WHs is deprecated and testEvent = { sampleData, sampleError } */}
          <TabbedCodeBlock
            styles={{ maxHeight: '700px' }}
            tabs={[
              {
                label: staticText.webhookDetails.payload.title,
                code: JSON.stringify(testEvent && (testEvent.sampleData || testEvent), null, 2),
                error: errorFetchingTestEvent && staticText.webhookDetails.payload.fetchError,
              },
              {
                label: staticText.webhookDetails.payload.errorTitle,
                code: (testEvent && testEvent.sampleError)
                ? JSON.stringify(testEvent.sampleError, null, 2) : null,
              },
            ]}
          />
        </Box>
      </Box>
    </React.Fragment>
  );
};

export default WebhookDetails;

WebhookDetails.propTypes = {
  applicationId: string.isRequired,
  heading: string.isRequired,
  webhook: shape({
    name: string.isRequired,
    id: string.isRequired,
    status: string.isRequired,
    callbackUri: string.isRequired,
    errorCallbackUrl: string,
    signalNames: arrayOf(string),
    permissions: arrayOf(string),
    units: string,
    updatedAt: string,
  }).isRequired,
  signals: shape({
    [string]: arrayOf(shape({
      name: string.isRequired,
      groupName: string.isRequired,
      description: string.isRequired,
    })).isRequired,
  }).isRequired,
  testEvent: object,
  fetchTestEventRequest: func.isRequired,
  errorFetchingTestEvent: bool.isRequired,
  toggleWebhookModal: func.isRequired,
};

WebhookDetails.defaultProps = {
  testEvent: null,
};
